import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FacebookAd } from "@/hooks/api/use-brand-ads";
import { useState } from "react";
import {
  Bar,
  BarChart,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

// Constants for media types and their properties
const MEDIA_TYPES = {
  VIDEO: "VIDEO",
  IMAGE: "IMAGE",
  CAROUSEL: "CAROUSEL",
  DPA: "DPA",
} as const;

type MediaType = keyof typeof MEDIA_TYPES;
type MediaTypeKey = Lowercase<MediaType>;

// Color schema for different media types
const MEDIA_COLORS = {
  video: {
    primary: "#818cf8", // indigo-400
  },
  image: {
    primary: "#ec4899", // pink-500
  },
  carousel: {
    primary: "#22d3ee", // cyan-400
  },
  dpa: {
    primary: "#fb923c", // orange-400
  },
};

/**
 * Determines the actual media type for DCO ads by examining media URLs
 */
const getDcoMediaType = (ad: FacebookAd): MediaTypeKey => {
  const snapshot = ad.latest_snapshot;
  if (!snapshot) return "image"; // Default to image if no snapshot

  // Check if main snapshot has video
  if (snapshot.video_hd_url) {
    return "video";
  }

  // Check if any cards have videos
  const hasVideoInCards = snapshot.cards?.some((card) => card.video_hd_url);
  if (hasVideoInCards) {
    return "video";
  }

  // Default to image if no videos found
  return "image";
};

/**
 * Gets the effective media type for an ad, handling DCO specially
 */
const getEffectiveMediaType = (
  ad: FacebookAd
): { type: string; display: string; isDco: boolean } => {
  const format = ad.latest_snapshot?.display_format;
  const displayName = ad.latest_snapshot?.display_format_display || format;

  if (!format) {
    return { type: "IMAGE", display: "Image", isDco: false };
  }

  // For DCO ads, determine if they're using images or videos
  if (format === "DCO") {
    const mediaType = getDcoMediaType(ad);
    return {
      type: mediaType.toUpperCase() as MediaType,
      display: mediaType === "video" ? "Video (DCO)" : "Image (DCO)",
      isDco: true,
    };
  }

  // Format display names for specific types
  if (format === "DPA") {
    return { type: format, display: "DPA", isDco: false };
  }

  return { type: format, display: displayName || format, isDco: false };
};

// Helper function to get date from X days ago
const getDateXDaysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

// Helper function to group ads by week with time range filter and media type breakdown
const getWeeklyAdCounts = (ads: FacebookAd[], timeRange: "4weeks" | "all") => {
  if (!ads?.length && timeRange === "all") return [];

  // Filter ads based on time range
  const filteredAds =
    timeRange === "4weeks"
      ? ads.filter((ad) => new Date(ad.start_date!) >= getDateXDaysAgo(28))
      : [...ads]; // Use all ads for "all" option

  if (!filteredAds.length && timeRange === "all") return [];

  // Format date as MM/DD
  const formatDate = (date: Date) => {
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // For "4weeks" option, always show exactly 4 weeks
  if (timeRange === "4weeks") {
    const weeks: { start: Date; end: Date }[] = [];
    const fourWeeksAgo = getDateXDaysAgo(28);

    // Create 4 weeks starting from 4 weeks ago
    for (let i = 0; i < 4; i++) {
      const weekStart = new Date(fourWeeksAgo);
      weekStart.setDate(weekStart.getDate() + i * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      weeks.push({ start: weekStart, end: weekEnd });
    }

    // Count ads per week with media type breakdown
    return weeks.map((week) => {
      const adsInWeek = filteredAds.filter((ad) => {
        const adDate = new Date(ad.start_date!);
        return adDate >= week.start && adDate <= week.end;
      });

      // Group by media type
      const mediaTypeCounts = {
        video: 0,
        image: 0,
        carousel: 0,
        dpa: 0,
      };

      adsInWeek.forEach((ad) => {
        const { type } = getEffectiveMediaType(ad);
        const key = type.toLowerCase() as MediaTypeKey;
        if (mediaTypeCounts[key] !== undefined) {
          mediaTypeCounts[key]++;
        }
      });

      return {
        name: `${formatDate(week.start)}`,
        count: adsInWeek.length,
        ...mediaTypeCounts,
      };
    });
  }

  // For "all" option, use the original logic with media type breakdown
  // Get the date range
  const dates = filteredAds.map((ad) => new Date(ad.start_date!).getTime());
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  // Create an array of week boundaries
  const weeks: { start: Date; end: Date }[] = [];
  const currentDate = new Date(minDate);

  while (currentDate <= maxDate) {
    const weekStart = new Date(currentDate);
    const weekEnd = new Date(currentDate);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weeks.push({ start: weekStart, end: weekEnd });
    currentDate.setDate(currentDate.getDate() + 7);
  }

  // Count ads per week with media type breakdown
  return weeks.map((week) => {
    const adsInWeek = filteredAds.filter((ad) => {
      const adDate = new Date(ad.start_date!);
      return adDate >= week.start && adDate <= week.end;
    });

    // Group by media type
    const mediaTypeCounts = {
      video: 0,
      image: 0,
      carousel: 0,
      dpa: 0,
    };

    adsInWeek.forEach((ad) => {
      const { type } = getEffectiveMediaType(ad);
      const key = type.toLowerCase() as MediaTypeKey;
      if (mediaTypeCounts[key] !== undefined) {
        mediaTypeCounts[key]++;
      }
    });

    return {
      name: `${formatDate(week.start)}`,
      count: adsInWeek.length,
      ...mediaTypeCounts,
    };
  });
};

// Custom tooltip component for dark theme with media type breakdown
const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    // Calculate total by summing all media type values
    const total = payload.reduce((sum, entry) => {
      // Only include media type entries (video, image, carousel, dpa)
      if (entry.dataKey !== "count") {
        return sum + ((entry.value as number) || 0);
      }
      return sum;
    }, 0);

    return (
      <div className="bg-card border border-border rounded-md shadow-lg p-3">
        <p className="text-sm font-medium text-foreground">{`Week of ${label}`}</p>
        {payload.map((entry, index) => {
          // Skip the total count entry
          if (entry.dataKey === "count") return null;

          // Only show media types with values > 0
          if (entry.value === 0) return null;

          const mediaType = entry.dataKey as MediaTypeKey;
          const displayName =
            mediaType.charAt(0).toUpperCase() + mediaType.slice(1);

          return (
            <p
              key={`item-${index}`}
              className="text-sm"
              style={{ color: entry.color }}
            >
              <span className="font-semibold">{`${entry.value}`}</span>{" "}
              {displayName}
            </p>
          );
        })}
        <p className="text-sm text-primary mt-1 pt-1 border-t border-border">
          <span className="font-semibold">{`${total}`}</span> Total
        </p>
      </div>
    );
  }

  return null;
};

interface AdsLaunchedChartProps {
  ads: FacebookAd[];
}

export default function AdsLaunchedChart({ ads }: AdsLaunchedChartProps) {
  const [timeRange, setTimeRange] = useState<"4weeks" | "all">("4weeks");

  const data = getWeeklyAdCounts(ads, timeRange);

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Ads Launched per Week</h3>
        <Select
          value={timeRange}
          onValueChange={(value) => setTimeRange(value as "4weeks" | "all")}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Time Range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="4weeks">Last 4 Weeks</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="w-full h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 5, right: 5, left: -30, bottom: -20 }}
            barGap={0}
            barCategoryGap="10%"
          >
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              domain={[0, "auto"]}
              allowDecimals={false}
              tick={{ fontSize: 10 }}
            />
            <RechartsTooltip
              content={<CustomTooltip />}
              cursor={{ fill: "transparent" }}
            />
            <Legend wrapperStyle={{ fontSize: "12px" }} />
            <Bar
              dataKey="video"
              name="Video"
              stackId="a"
              fill={MEDIA_COLORS.video.primary}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="image"
              name="Image"
              stackId="a"
              fill={MEDIA_COLORS.image.primary}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="carousel"
              name="Carousel"
              stackId="a"
              fill={MEDIA_COLORS.carousel.primary}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="dpa"
              name="DPA"
              stackId="a"
              fill={MEDIA_COLORS.dpa.primary}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
